import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import React, { Component } from "react";
import { UnitSystem } from "@my-dld/utils/UnitUtils";
import { getAppServices } from "@10x/coreapp";
import ListGridDataView from "@my-dld/components/DataView/ListGridDataView";
import PropertyTable from "@my-dld/components/Portfolio/PropertyTable";
import { TabuPropertyType } from "@my-dld/utils/PropertyUtils";
import PropertyCard from "@my-dld/components/Portfolio/PropertyCard";
import { AssetCard } from "@10x/dubai-rest-api/dist/property_registry/entity";
import { MyRequestServices } from "@my-dld/utils/TabuUtils";
import { ITabuSmartService, TabuRequestApi, TabuRequestUtils } from "@10x/tabu-mobile-api";
import { LocaleUtil } from "@eres/core";
import { DashboardBCItem } from "@my-dld/contexts/PageContext";
import PageTitle from "@my-dld/components/PageTitle";
import Strings from "@my-dld/assets/strings";
import { navigate } from "@my-dld/utils/StoreUtils";
import { TabuRequestContext } from "@my-dld/contexts/TabuRequestContext";
import { Redirect } from "react-router";
import { hideLoading, showErrorMessage, showLoading, showMessage } from "@my-dld/utils/UIUtils";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { ValidateRequest } from "@10x/tabu-mobile-api/dist/requests/entity";
import { getParticipantID } from "@10x/sso";
import { getTabuErrorMessage } from "./BaseTabuRequestForm";

interface Props extends RoutedViewProps<{ serviceId: string }> {

}

interface State {
	serviceId?: number;
	properties: AssetCard[];
	unit: UnitSystem;
}

class MultiPropertiesForm extends Component<Props, State> {
	static contextType = TabuRequestContext;
	context!: React.ContextType<typeof TabuRequestContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			serviceId: Number( props.match.params.serviceId ),
			properties: [],
			unit: getAppServices().storeService.getStore().getState().User.unit,
		};
	}

	componentDidMount() {
		this.setDataFromContext();
	}

	render() {
		if ( this.context.property == null ) {
			return <Redirect to={ "/mydld" }/>;
		}
		const { serviceId, properties } = this.state;
		const title = getTitle( serviceId );
		return <ListGridDataView
			isLoading={ false }
			dataAvailable={ properties && properties.length > 0 }
			title={ `${ title } - ${ Strings.selected_properties }` }
			onUnitSystemChange={ this.onUnitChange }
			renderListView={ this.renderListView }
			renderGridView={ this.renderGridView }
			hidePageTitle={ true }>
			<PageTitle paths={ [ DashboardBCItem ] } title={ title }/>
			<div className="btn-alignment">
                <span className="alert alert-warning">
                    { Strings.you_need_minimum_two_properties_for_this_procedure }
                </span>
				<div>
					<button
						className="btn btn_1" type="button"
						onClick={ this.onAddMore }>{ Strings.add_more_properties }</button>
					{ properties.length > 1 &&
						<button
							className="btn btn_1 ms-2" type="button"
							onClick={ this.onContinue }>{ Strings.proceed }</button> }
				</div>
			</div>
		</ListGridDataView>;
	}

	private setDataFromContext() {
		const { property, properties } = this.context;
		this.setState( {
			properties: [ property!, ...properties ],
		} );
	}

	private onUnitChange = ( unit: UnitSystem ) => {
		this.setState( { unit: unit } );
	};

	private renderListView = () => {
		const { property } = this.context;
		const { properties, unit } = this.state;
		return <>
			{ property?.propertyTypeId === TabuPropertyType.LAND &&
				<PropertyTable
					type={ TabuPropertyType.LAND } unit={ unit }
					properties={ properties } isSelectable={ true }
					masterProperty={ property } sProperties={ properties }
					onSelect={ this.onSelect }/> }
			{ property?.propertyTypeId === TabuPropertyType.VILLA &&
				<PropertyTable
					type={ TabuPropertyType.VILLA } unit={ unit }
					properties={ properties } isSelectable={ true }
					masterProperty={ property } sProperties={ properties }
					onSelect={ this.onSelect }/>
			}
			{ property?.propertyTypeId === TabuPropertyType.UNIT &&
				<PropertyTable
					type={ TabuPropertyType.UNIT } unit={ unit }
					properties={ properties } isSelectable={ true }
					masterProperty={ property } sProperties={ properties }
					onSelect={ this.onSelect }/>
			}
		</>;
	};

	private renderGridView = () => {
		const { property } = this.context;
		const { properties, unit } = this.state;
		return <React.Fragment>
			{ property?.propertyTypeId === TabuPropertyType.LAND && <div className="land">
				<PropertyCard
					type={ TabuPropertyType.LAND } unit={ unit }
					properties={ properties } isSelectable={ true }
					masterProperty={ property } sProperties={ properties }
					onSelect={ this.onSelect }/>
			</div> }
			{ property?.propertyTypeId === TabuPropertyType.VILLA && <div className="villa">
				<PropertyCard
					type={ TabuPropertyType.VILLA } unit={ unit }
					properties={ properties } isSelectable={ true }
					masterProperty={ property } sProperties={ properties }
					onSelect={ this.onSelect }/>
			</div> }
			{ property?.propertyTypeId === TabuPropertyType.UNIT && <div className="unit">
				<PropertyCard
					type={ TabuPropertyType.UNIT } unit={ unit }
					properties={ properties } isSelectable={ true }
					masterProperty={ property } sProperties={ properties }
					onSelect={ this.onSelect }/>
			</div> }
		</React.Fragment>;
	};

	private onSelect = ( property: AssetCard, checked: boolean ) => {
		const { properties } = this.state;
		if ( !checked ) {
			this.setState( {
				properties: properties.filter( p => p.propertyId !== property.propertyId ),
			} );
			this.context.removeProperty( property );
		}
	};

	private onAddMore = () => {
		const { serviceId } = this.state;
		navigate( `/mydld/taburequest/${ serviceId }/add_properties` );
	};

	private onContinue = () => {
		if ( this.context.reqDetails ) {
			this.props.history.goBack();
		} else {
			this.validateTabuService();
		}
	};

	private validateTabuService() {
		showLoading();
		const { property } = this.context;
		const { serviceId, properties } = this.state;
		const propertyIds = properties.map( x => {
			return { PropertyId: x.propertyId, PropertyTypeId: x.propertyTypeId };
		} );
		const request: ValidateRequest = {
			ProcedureId: serviceId!,
			PropertyWithTypeIds: propertyIds,
			additionalData: [ {
				key: "ParticipantId",
				value: getParticipantID()!.toString(),
			}, ],
		};
		const ids = propertyIds.reduce( ( previousValue, currentValue ) => {
			return previousValue + currentValue.PropertyId;
		}, "" );
		TabuRequestApi.validateTabuService( request ).then( response => {
			hideLoading();
			if ( response && response.Response && response.Response.length > 0 ) {
				const messages = response.Response.map( value => {
					return getTabuErrorMessage( value );
				} );
				const message = messages.join( "\n" );
				showMessage( message );
				trackEventWithData( "Tabu", "PropertyValidationFailed",
					{ property: ids, service: "" + serviceId, message: message } );
			} else {
				const path = TabuRequestUtils.tabuProcedureIdString( Number( serviceId ) );
				navigate( `/mydld/taburequest/${ path }/${ property?.propertyTypeId }/${ property?.propertyId }` );
			}
		} ).catch( ( reason ) => {
			showErrorMessage( reason );
			trackEventWithData( "Tabu", "PropertyValidationFailed",
				{ property: ids, service: "" + serviceId, message: reason } );
		} );
	}
}

function getTitle( serviceId?: number ) {
	const service: ITabuSmartService | undefined = MyRequestServices.find( ( value ) => value.Id === serviceId );
	return LocaleUtil.getLocalValue( service?.Name );
}

export default MultiPropertiesForm;
